import {
	TSheetFolder,
	ISheetFolderForm,
	ISheetFileForm,
	TSheetFile,
	TSheetFileVersion,
	SheetFormSubmissionAction,
} from "./store"
import {
	Alert,
	Box,
	Button,
	Icons,
	Inline,
	SelectInput,
	Spinner,
	Stack,
	Text,
	Time,
} from "@sembark-travel/ui/base"
import {
	ButtonLink,
	NavigationPromptDialog,
	useNavigationPrompt,
} from "@sembark-travel/ui/router"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import useSWR from "swr"
import { useCallback, useMemo, useState } from "react"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui/dialog"
import {
	Form,
	SubmissionError,
	TextInputField,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { showSnackbar } from "@sembark-travel/ui/snackbar"

export function CreateSheetFileWithInFolder<
	TData = unknown,
	ISheetForm = unknown,
>({
	service,
	onSuccess,
	children,
}: {
	service: {
		saveSheetVersionDetails: (
			xhr: XHRInstance,
			folderId: string,
			fileName: string,
			sheetData: ISheetForm
		) => Promise<TSheetFileVersion<TData>>

		uploadSheetVersion: (
			xhr: XHRInstance,
			sheetVersionId: string
		) => Promise<TSheetFileVersion<TData>>
	}
	onSuccess: (foldeId: string, fileId: string, versionId: string) => void
	children: (props: {
		createSheetFile: (
			folderId: string,
			fileName: string,
			sheetData: ISheetForm,
			action: SheetFormSubmissionAction
		) => Promise<void>
	}) => React.ReactNode
}) {
	const { blocker, disablePrompt, enablePrompt } = useNavigationPrompt(true)
	const xhr = useXHR()

	const createSheetFile = useCallback(
		async (
			folderId: string,
			fileName: string,
			sheetData: ISheetForm,
			action: SheetFormSubmissionAction
		) => {
			const resp = await service.saveSheetVersionDetails(
				xhr,
				folderId,
				fileName,
				sheetData
			)

			if (action === SheetFormSubmissionAction.UPLOAD) {
				if (!resp?.id) {
					throw new Error("No sheet id found to upload.")
				}

				await service.uploadSheetVersion(xhr, String(resp.id))
			}

			if (action === SheetFormSubmissionAction.UPLOAD) {
				showSnackbar("Sheet uploaded successfully.")
			} else {
				showSnackbar("Sheet saved to draft successfully.")
			}

			const sheetFileVersionId = String(resp.id)
			const sheetFileId = String(resp.file?.id)
			const sheetFolderId = String(resp.file?.folder?.id)
			disablePrompt()
			onSuccess(sheetFolderId, sheetFileId, sheetFileVersionId)
			enablePrompt()
		},
		[service, xhr, onSuccess, disablePrompt, enablePrompt]
	)

	return (
		<Box>
			<NavigationPromptDialog blocker={blocker} />
			{children({
				createSheetFile,
			})}
		</Box>
	)
}

export function EditSheetFolderInDialog({
	children,
	folder,
	onSuccess,
	editFolderDetails,
}: {
	children: (props: { showEditDialog: () => void }) => React.ReactNode
	folder: TSheetFolder
	onSuccess?: () => void
	editFolderDetails(
		xhr: XHRInstance,
		sheetFolderData: ISheetFolderForm,
		folderId: string
	): Promise<TSheetFolder>
}) {
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()

	const editCabMetricSheetFolder = useCallback(
		async (sheetFolderData: ISheetFolderForm) => {
			await editFolderDetails(xhr, sheetFolderData, String(folder.id))
			onSuccess?.()
			closeDialog()
		},
		[xhr, folder, closeDialog, onSuccess, editFolderDetails]
	)

	return (
		<>
			{children({ showEditDialog: openDialog })}
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Edit Folder Details"
				sm
			>
				<Dialog.Body>
					<SheetFolderForm
						onSubmit={editCabMetricSheetFolder}
						onCancel={closeDialog}
						initialValues={{
							name: folder.name,
							description: folder?.description,
						}}
						editMode={true}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function SheetFolderForm({
	onSubmit,
	onCancel,
	initialValues,
	editMode = false,
}: {
	onSubmit: (folderData: ISheetFolderForm) => Promise<void>
	onCancel: () => void
	initialValues: { name: string; description?: string }
	editMode?: boolean
}) {
	return (
		<Form<ISheetFolderForm>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="4">
						<TextInputField name="name" label="Folder Name" required />
						<TextInputField name="description" label="Description" />
						<SubmissionError />
						<Inline gap="4" justifyContent="end">
							<Button
								disabled={submitting}
								color="secondary"
								onClick={onCancel}
							>
								Cancel
							</Button>
							<Button disabled={submitting} type="submit">
								{submitting
									? "Please wait..."
									: (editMode ? "Save" : "Create") + " Folder"}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function EditSheetFileInDialog({
	file,
	children,
	onSuccess,
	editFileDetails,
}: {
	file: TSheetFile
	children: (props: { showEditDialog: () => void }) => React.ReactNode
	onSuccess?: () => void
	editFileDetails(
		xhr: XHRInstance,
		sheetFileData: ISheetFileForm,
		fileId: string
	): Promise<TSheetFile>
}) {
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()

	const editCabMetricSheetFile = useCallback(
		async (sheetFileData: ISheetFileForm) => {
			await editFileDetails(xhr, sheetFileData, String(file.id))
			onSuccess?.()
			closeDialog()
		},
		[xhr, file, closeDialog, onSuccess, editFileDetails]
	)

	return (
		<>
			{children({ showEditDialog: openDialog })}
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Edit File Details"
				sm
			>
				<Dialog.Body>
					<SheetFileForm
						onSubmit={editCabMetricSheetFile}
						onCancel={closeDialog}
						initialValues={{
							name: file?.name || "",
							description: file?.description,
						}}
						editMode={true}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function SheetFileForm({
	onSubmit,
	onCancel,
	initialValues,
	editMode = false,
}: {
	onSubmit: (fileData: ISheetFileForm) => Promise<void>
	onCancel: () => void
	initialValues: { name: string; description: string | null }
	editMode?: boolean
}) {
	return (
		<Form<ISheetFileForm>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="4">
						<TextInputField name="name" label="File Name" required />
						<TextInputField name="description" label="Description" />
						<SubmissionError />
						<Inline gap="4" justifyContent="end">
							<Button
								disabled={submitting}
								color="secondary"
								onClick={onCancel}
							>
								Cancel
							</Button>
							<Button disabled={submitting} type="submit">
								{submitting
									? "Please wait..."
									: (editMode ? "Save" : "Create") + " File"}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function DeleteSheetFileInDialog({
	file,
	onSuccess,
	children,
	deleteSheetFile,
}: {
	file: TSheetFile
	onSuccess?: () => void
	children: (props: {
		onDelete: () => void
		isDeleting: boolean
	}) => React.ReactNode
	deleteSheetFile(
		xhr: XHRInstance,
		fileId: string
	): Promise<{ message: string }>
}) {
	const [isDeleting, setIsDeleting] = useState(false)
	const xhr = useXHR()

	const deleteCabMetricSheetFile = useCallback(
		async (fileId: number): Promise<string> => {
			const { message } = await deleteSheetFile(xhr, String(fileId))

			return message
		},
		[xhr, deleteSheetFile]
	)

	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					{children({
						isDeleting,
						onDelete: async () => {
							if (isDeleting) return
							if (await confirm()) {
								setIsDeleting(true)

								try {
									const message = await deleteCabMetricSheetFile(file.id)

									showSnackbar(message)
									onSuccess?.()
								} catch (error) {
									const e = error as Error
									alert(e.message || "Something went wrong")
								} finally {
									setIsDeleting(false)
								}
							}
						},
					})}
					<ConfirmationDialog
						title="Confirmation"
						destructiveAction
						acceptActionLabel="I understand, Delete It"
					>
						<Stack gap="2">
							<Text fontWeight="semibold" color="warning">
								<Icons.Attention /> This action can not be reversed.
							</Text>
							<Text>Are you sure you want to delete {file.name || ""}?</Text>
						</Stack>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}

export function ReadSheetFileVersionDetails<
	TData = unknown,
	ISheetForm = unknown,
>({
	sheetVersionId,
	sheetId,
	sheetFolderId,
	service,
	getLinkToEdit,
	transformDataToFormValues,
	children,
}: {
	sheetFolderId: string
	sheetId: string
	sheetVersionId: string
	service: {
		fetchSheetVersionDetails: (
			xhr: XHRInstance,
			fileVersionId: string
		) => Promise<TSheetFileVersion<TData>>
	}
	getLinkToEdit: (folderId: string, fileId: string, versionId: string) => string
	transformDataToFormValues: (sheetData: TData) => ISheetForm
	children: (props: { sheetValues: ISheetForm | undefined }) => React.ReactNode
}) {
	const xhr = useXHR()
	const { data: sheetVersionDetails, isLoading } = useSWR<
		TSheetFileVersion<TData>
	>(`data-drive/file-versions/${sheetVersionId}`, () => {
		return service.fetchSheetVersionDetails(xhr, sheetVersionId)
	})

	const sheetValues = useMemo<ISheetForm | undefined>(() => {
		if (!sheetVersionDetails?.data) return

		return transformDataToFormValues(sheetVersionDetails.data)
	}, [sheetVersionDetails, transformDataToFormValues])

	if (isLoading && !sheetVersionDetails)
		return <Spinner alignCenter padding="4" />

	if (!sheetVersionDetails)
		return (
			<Alert status="error">
				This file does not have the specified version.
			</Alert>
		)

	return (
		<Box padding={"1"}>
			<Stack
				key={sheetVersionDetails.id}
				paddingX={"2"}
				width="full"
				gap={"1"}
				backgroundColor={"default"}
			>
				<Inline gap="2">
					{sheetVersionDetails.is_draft ? (
						<Inline
							borderRadius="md"
							padding={"2"}
							bgColor={"warning"}
							alignItems={"center"}
							borderWidth={"1"}
							borderColor={"warning"}
						>
							<Text fontSize={"xs"} fontWeight="semibold" color={"warning"}>
								Draft Version
							</Text>
						</Inline>
					) : null}

					{!sheetVersionDetails.file?.deleted_at ? (
						<ButtonLink
							to={getLinkToEdit(
								sheetFolderId,
								sheetId,
								String(sheetVersionDetails.id)
							)}
							query={{
								file: sheetVersionDetails.file?.name,
								folder: sheetVersionDetails.file?.folder?.name,
							}}
							level="secondary"
						>
							<Icons.Pencil />
							Edit Sheet
						</ButtonLink>
					) : null}
				</Inline>
				<Inline gap={"1"}>
					<Text fontSize={"sm"} color={"muted"}>
						Created on <Time timestamp={sheetVersionDetails.created_at} />
						{sheetVersionDetails.created_by?.name
							? ` by ${sheetVersionDetails.created_by?.name}`
							: ""}
						.
					</Text>
					{sheetVersionDetails.uploaded_at &&
					sheetVersionDetails.uploaded_by ? (
						<Text fontSize={"sm"} color={"muted"}>
							Uploaded by {sheetVersionDetails.uploaded_by.name} on{" "}
							<Time timestamp={sheetVersionDetails.uploaded_at} />
						</Text>
					) : null}
				</Inline>
			</Stack>

			<Box>{children({ sheetValues })}</Box>
		</Box>
	)
}

export function EditSheetFileVersionDetails<
	TData = unknown,
	ISheetForm = unknown,
>({
	sheetFolderId,
	sheetId,
	sheetVersionId,
	xhrFetchBaseUrl,
	service,
	transformDataToFormValues,
	onUploadSuccess,
	onDraftSuccess,
	children,
}: {
	sheetFolderId: string
	sheetId: string
	sheetVersionId: string
	xhrFetchBaseUrl: string
	service: {
		fetchSheetVersionDetails: (
			xhr: XHRInstance,
			fileVersionId: string
		) => Promise<TSheetFileVersion<TData>>
		saveSheetVersionDetails: (
			xhr: XHRInstance,
			folderId: string,
			fileName: string,
			sheetData: ISheetForm
		) => Promise<TSheetFileVersion<TData>>
		uploadSheetVersion: (
			xhr: XHRInstance,
			sheetVersionId: string
		) => Promise<TSheetFileVersion<TData>>
	}
	transformDataToFormValues: (sheetData: TData) => ISheetForm
	onUploadSuccess?: (
		folderId: string,
		fileId: string,
		fileVersionId: string
	) => void
	onDraftSuccess?: (
		folderId: string,
		fileId: string,
		fileVersionId: string
	) => void
	children: (props: {
		sheetValues: ISheetForm | undefined
		onUpdateSheetFile: (
			sheetData: ISheetForm,
			action: SheetFormSubmissionAction,
			isSheetUpdated?: boolean
		) => Promise<void>
	}) => React.ReactNode
}) {
	const { blocker, disablePrompt, enablePrompt } = useNavigationPrompt(true)
	const xhr = useXHR()

	const { data: sheetVersionDetails, error } = useSWR<TSheetFileVersion<TData>>(
		`${xhrFetchBaseUrl}/file-versions/${sheetVersionId}`,
		() => {
			return service.fetchSheetVersionDetails(xhr, sheetVersionId)
		}
	)

	const onUpdateSheetFile = useCallback(
		async (
			sheetData: ISheetForm,
			action: SheetFormSubmissionAction,
			isSheetUpdated?: boolean
		) => {
			let resp: TSheetFileVersion<TData> | null = null

			if (action === SheetFormSubmissionAction.SAVE_DRAFT) {
				resp = await service.saveSheetVersionDetails(
					xhr,
					sheetFolderId,
					sheetVersionDetails?.file?.name || "",
					sheetData
				)

				if (resp.id !== Number(sheetVersionId)) {
					showSnackbar("New Draft file created successfully.")
				} else {
					showSnackbar("Draft saved successfully.")
				}

				const folderId = String(resp.file?.folder?.id || sheetFolderId)
				const fileId = String(resp.file?.id || sheetId)
				const fileVersionId = String(resp.id || sheetVersionId)
				disablePrompt()
				onDraftSuccess?.(folderId, fileId, fileVersionId)
				enablePrompt()
			}

			if (action === SheetFormSubmissionAction.UPLOAD) {
				if (
					sheetVersionId ===
						String(sheetVersionDetails?.file?.latest_version?.id || "") &&
					!sheetVersionDetails?.file?.latest_version?.is_draft
				) {
					throw Error(
						"Sheet is already uploaded. Save as Draft to create a new version and try again."
					)
				}

				if (isSheetUpdated) {
					await service.saveSheetVersionDetails(
						xhr,
						sheetFolderId,
						sheetVersionDetails?.file?.name || "",
						sheetData
					)
				}

				resp = await service.uploadSheetVersion(xhr, sheetVersionId)

				showSnackbar("Sheet uploaded successfully.")

				const folderId = String(resp.file?.folder?.id || sheetFolderId)
				const fileId = String(resp.file?.id || sheetId)
				const fileVersionId = String(resp.id || sheetVersionId)
				disablePrompt()
				onUploadSuccess?.(folderId, fileId, fileVersionId)
				enablePrompt()
			}
		},
		[
			sheetVersionDetails,
			sheetFolderId,
			sheetId,
			sheetVersionId,
			service,
			xhr,
			onDraftSuccess,
			onUploadSuccess,
			disablePrompt,
			enablePrompt,
		]
	)

	const sheetValues = useMemo<ISheetForm | undefined>(() => {
		if (!sheetVersionDetails?.data) return

		return transformDataToFormValues(sheetVersionDetails.data)
	}, [sheetVersionDetails, transformDataToFormValues])

	if (!sheetId && !sheetFolderId) {
		return <Alert status="error">Sheet file content not found.</Alert>
	}

	if (!sheetVersionDetails) {
		if (error) {
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}

	return (
		<Stack padding={"2"} gap={"2"}>
			<Stack
				key={sheetVersionDetails.id}
				borderWidth={"1"}
				borderRadius="md"
				padding="4"
				width="full"
				gap={"6"}
				backgroundColor={"default"}
			>
				<Inline justifyContent={"between"}>
					<Stack gap="4">
						<Inline gap="2">
							<Icons.DocumentDownload size={"6"} />
							<Text fontSize={"md"}>{sheetVersionDetails.file?.name}</Text>
						</Inline>
					</Stack>

					{sheetVersionDetails.created_at ? (
						<Box fontSize={"sm"} color={"muted"}>
							Created on <Time timestamp={sheetVersionDetails.created_at} />
						</Box>
					) : null}
				</Inline>
			</Stack>

			<Box>
				{children({
					sheetValues,
					onUpdateSheetFile,
				})}
			</Box>
			<NavigationPromptDialog blocker={blocker} />
		</Stack>
	)
}

export function CompareSheetFileVersions<
	TData = unknown,
	ISheetForm = unknown,
>({
	sheetId,
	sheetVersionId1,
	sheetVersionId2,
	xhrFetchBaseUrl,
	service,
	transformDataToFormValues,
	onCompareWithSheetChange,
	children,
}: {
	sheetId: string
	sheetVersionId1: string
	sheetVersionId2: string
	xhrFetchBaseUrl: string
	service: {
		fetchSheetDetails: (xhr: XHRInstance, fileId: string) => Promise<TSheetFile>
		fetchSheetVersionDetails: (
			xhr: XHRInstance,
			fileVersionId: string
		) => Promise<TSheetFileVersion<TData>>
	}
	transformDataToFormValues: (sheetData: TData) => ISheetForm
	onCompareWithSheetChange: (
		folderId: string,
		folderName: string,
		fileId: string,
		fileName: string,
		versionId1: string,
		versionId2: string
	) => void
	children: (props: {
		sheet1Values: ISheetForm | undefined
		sheet2Values: ISheetForm | undefined
	}) => React.ReactNode
}) {
	const xhr = useXHR()

	const { data: fileDetails, error: fileError } = useSWR<TSheetFile>(
		`${xhrFetchBaseUrl}/files/${sheetId}`,
		() => service.fetchSheetDetails(xhr, sheetId)
	)

	const { data: sheetVersion1Details, error: fileVersion1Error } = useSWR<
		TSheetFileVersion<TData>
	>(`${xhrFetchBaseUrl}/file-versions/${sheetVersionId1}`, () => {
		return service.fetchSheetVersionDetails(xhr, sheetVersionId1)
	})
	const { data: sheetVersion2Details, error: fileVersion2Error } = useSWR<
		TSheetFileVersion<TData>
	>(`${xhrFetchBaseUrl}/file-versions/${sheetVersionId2}`, () => {
		return service.fetchSheetVersionDetails(xhr, sheetVersionId2)
	})

	const sheetValuesSheet1 = useMemo<ISheetForm | undefined>(() => {
		if (!sheetVersion1Details?.data) return

		return transformDataToFormValues(sheetVersion1Details.data)
	}, [sheetVersion1Details, transformDataToFormValues])

	const sheetValuesSheet2 = useMemo<ISheetForm | undefined>(() => {
		if (!sheetVersion2Details?.data) return

		return transformDataToFormValues(sheetVersion2Details.data)
	}, [sheetVersion2Details, transformDataToFormValues])

	if (!sheetVersion1Details && !sheetVersion2Details) {
		if (fileError || fileVersion1Error || fileVersion2Error) {
			return (
				<Alert status="error">
					Something went wrong. Please try after sometime.
				</Alert>
			)
		}

		return <Spinner alignCenter padding="4" />
	}

	if (!fileDetails) {
		if (fileError) {
			return <Alert status="error">Sheet file content not found.</Alert>
		}

		return <Spinner alignCenter padding="4" />
	}

	return (
		<Stack padding={"2"} gap={"2"}>
			<Stack
				key={fileDetails.id}
				borderWidth={"1"}
				borderRadius="md"
				padding="4"
				width="full"
				gap={"6"}
				backgroundColor={"default"}
			>
				<Inline justifyContent={"between"}>
					<Stack gap="4">
						<Inline gap="2" alignItems={"center"}>
							<Inline gap="2" alignItems={"center"}>
								<Icons.Sheet color={"primary"} size={"6"} />
								<Text fontSize={"md"}>{fileDetails.name}</Text>
							</Inline>
						</Inline>
						{fileDetails.updated_at && fileDetails.updated_by?.name ? (
							<Inline gap="2">
								<Text fontSize={"sm"} color={"muted"}>
									Last uploaded by {fileDetails.updated_by.name} on{" "}
									<Time timestamp={fileDetails.updated_at} />
								</Text>
							</Inline>
						) : null}
					</Stack>

					<Text fontSize={"sm"} color={"muted"}>
						Created on <Time timestamp={fileDetails.created_at} />
					</Text>
				</Inline>

				<Inline gap={"2"} alignItems={"center"}>
					<Text fontSize={"sm"}>Compare</Text>

					<Inline gap="2" alignItems={"center"}>
						<Inline
							borderRadius="md"
							paddingX={"2"}
							paddingY={"1"}
							bgColor={"success"}
							alignItems={"center"}
							borderWidth={"1"}
							borderColor={"success"}
						>
							<Text fontWeight="semibold" color={"success"}>
								V - {(fileDetails.versions || []).length}
							</Text>
						</Inline>
						<Text fontSize={"sm"}>With</Text>

						<SelectInput
							id="select_multiple_versions"
							value={sheetVersionId2}
							onChange={(e) => {
								const selectedVersionId = Number(e.currentTarget.value || 1)

								onCompareWithSheetChange(
									String(fileDetails.folder?.id),
									fileDetails.folder?.name || "",
									String(fileDetails.id),
									fileDetails?.name || "",
									String(fileDetails.latest_version?.id),
									String(selectedVersionId)
								)
							}}
						>
							{(fileDetails.versions || [])
								.filter(
									(version) => version.id !== fileDetails.latest_version?.id
								)
								.map((version, index) => (
									<option key={version.id} value={version.id}>
										V - {(fileDetails.versions || []).length - index - 1}
									</option>
								))}
						</SelectInput>
					</Inline>
				</Inline>
			</Stack>

			<Box
				style={{
					width: "calc(100vw - 32px)",
				}}
				overflowX="auto"
			>
				{children({
					sheet1Values: sheetValuesSheet1,
					sheet2Values: sheetValuesSheet2,
				})}
			</Box>
		</Stack>
	)
}
